import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  prepareUser,
  logout as logoutAction,
  setLoading,
  fetchUserProfile,
} from '@/store/slices/authSlice';
import {
  getMsalInstance,
  getCurrentAccount,
  getAccountInfo,
  getIdToken,
  loginRequest,
} from '@/services/msal';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import type { AuthenticationResult } from '@azure/msal-browser';

// Global flag to prevent multiple initializations
let isGlobalInitialized = false;
let initializationPromise: Promise<void> | null = null;

export function useMsalAuth() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector(state => state.auth);
  const [isInitialized, setIsInitialized] = useState(isGlobalInitialized);
  const [error, setError] = useState<string | null>(null);

  // Initialize MSAL and check for existing session (SSO)
  // Only initialize once globally, not per component
  useEffect(() => {
    // If already initialized globally, just mark this hook as initialized
    if (isGlobalInitialized) {
      setIsInitialized(true);
      return;
    }

    // If initialization is in progress, wait for it
    if (initializationPromise) {
      initializationPromise
        .then(() => {
          setIsInitialized(true);
        })
        .catch(() => {
          setIsInitialized(true);
        });
      return;
    }

    // Start initialization
    const initPromise = (async () => {
      try {
        const instance = await getMsalInstance();

        // If MSAL is not configured, mark as initialized but don't try to authenticate
        if (!instance) {
          isGlobalInitialized = true;
          return;
        }

        // Only set loading if we don't already have auth state (to avoid unnecessary loading)
        if (!isAuthenticated) {
          dispatch(setLoading(true));
        }

        // Check for accounts in cache (SSO detection)
        const accounts = instance.getAllAccounts();

        if (accounts.length > 0) {
          // User has existing session - SSO enabled
          const account = accounts[0];
          instance.setActiveAccount(account);

          const accountInfo = getAccountInfo(account);
          if (accountInfo && !user) {
            // No credentials in state – set credentials and fetch full profile
            const { token: idToken } = await getIdToken();
            if (idToken) {
              dispatch(prepareUser({ user: accountInfo, token: idToken }));
            }
          } else if (accountInfo && user) {
            // Already have credentials (e.g. rehydrated from storage) – still run getMe
            dispatch(fetchUserProfile());
          }
        }
        isGlobalInitialized = true;
      } catch (err) {
        console.error('MSAL initialization error:', err);
        isGlobalInitialized = true;
      } finally {
        dispatch(setLoading(false));
        initializationPromise = null;
      }
    })();

    initializationPromise = initPromise;

    // Mark as initialized after promise resolves
    initPromise
      .then(() => {
        setIsInitialized(true);
      })
      .catch(() => {
        setIsInitialized(true);
        setError('Failed to initialize authentication');
      });
  }, [dispatch, isAuthenticated, user]);

  const login = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      setError(null);
      const instance = await getMsalInstance();

      if (!instance) {
        throw new Error(
          'MSAL is not configured. Please configure VITE_CLIENT_ID and VITE_TENANT_ID in your environment variables.'
        );
      }

      // First, check if user is already signed in (SSO)
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        // User already has a session - use SSO
        const account = accounts[0];
        instance.setActiveAccount(account);
        const accountInfo = getAccountInfo(account);
        if (accountInfo) {
          const { token: idToken } = await getIdToken();
          if (idToken) {
            dispatch(prepareUser({ user: accountInfo, token: idToken }));
            return; // SSO successful, no need for interactive login
          }
        }
      }

      // No existing session, perform interactive login
      const response: AuthenticationResult =
        await instance.loginPopup(loginRequest);
      if (response.account) {
        const accountInfo = getAccountInfo(response.account);
        if (accountInfo) {
          dispatch(
            prepareUser({
              user: accountInfo,
              token: response.idToken,
            })
          );
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      // Don't throw to prevent unhandled promise rejection
    } finally {
      dispatch(setLoading(false));
    }
  };

  const logout = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      connectAdapter.resetSession();
      const instance = await getMsalInstance();

      if (instance) {
        const account = await getCurrentAccount();
        if (account) {
          await instance.logoutPopup({
            account: account,
          });
        }
      }

      dispatch(logoutAction());
    } catch (err) {
      console.error('Logout error:', err);
      setError(err instanceof Error ? err.message : 'Logout failed');
      // Still logout from Redux even if MSAL logout fails
      connectAdapter.resetSession();
      dispatch(logoutAction());
    } finally {
      dispatch(setLoading(false));
    }
  };

  const refreshToken = async (): Promise<string | null> => {
    const { token: idToken } = await getIdToken();
    return idToken;
  };

  return {
    isInitialized,
    login,
    logout,
    refreshToken,
    error,
  };
}
